import { call, put, takeLatest, take, race, select } from "redux-saga/effects";
import { PayloadAction } from "@reduxjs/toolkit";
import {
    createChatSessionRequest,
    createChatSessionSuccess,
    createChatSessionFailure,
    setChatSessionId,
} from "../slice/chatSessionSlice";
import { createChatSessionApi } from "@src/api/apiService";
import { PatientContext } from "../slice/patientContextSlice";
import { RootState } from "../rootReducer";

export function* createChatSession(action: PayloadAction<{ partner_project_id?: string; current_consultation_id?: string; latest_consultation_id?: string; total_consultation_count?: number; no_context?: boolean } | undefined>): Generator<any, void, any> {
    const controller = new AbortController();

    // If no explicit context was forwarded by the caller, read it from the store
    // so every createChatSessionRequest() automatically attaches the active patient context.
    // Skip this when no_context is true — the caller explicitly wants a plain session
    // (e.g. fallback after a patient-context session creation failure).
    let contextPayload: { partner_project_id?: string; current_consultation_id?: string; latest_consultation_id?: string; total_consultation_count?: number } | undefined = undefined;
    if (!action.payload?.no_context) {
        contextPayload = action.payload;
        if (!contextPayload) {
            const stored: PatientContext | null = yield select((state: RootState) => state.patientContext.context);
            if (stored) {
                contextPayload = {
                    partner_project_id: stored.partnerProjectId,
                    current_consultation_id: stored.currentConsultationId,
                    total_consultation_count: stored.totalConsultationCount,
                };
            }
        }
    }

    try {
        // Race the API call against the user navigating to an existing session.
        // If setChatSessionId fires first, abort the HTTP request so the backend
        // doesn't create an empty ghost session.
        const { response, navigated } = yield race({
            response: call(createChatSessionApi, contextPayload, controller.signal),
            navigated: take(setChatSessionId.type),
        });

        if (navigated) {
            controller.abort();
            return;
        }

        yield put(createChatSessionSuccess(response.data));
    } catch (error: any) {
        // Axios throws ERR_CANCELED when aborted via signal — not an error worth surfacing.
        if (error.code === "ERR_CANCELED") return;

        if (error.code === "ECONNABORTED") {
            console.log("⏰ createChatSession timeout");
            yield put(createChatSessionFailure("Timeout Error"));
        } else {
            yield put(createChatSessionFailure(error?.response?.data?.detail || error.message || "Unknown error"));
        }
    }
}

// takeLatest: if createChatSessionRequest fires again while one is in flight,
// the previous saga (and its HTTP request via AbortController) is cancelled.
export function* chatSessionSaga() {
    yield takeLatest(createChatSessionRequest.type, createChatSession);
}
